import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { LABEL_KIND_LABEL_HU } from '@persped/shared';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useConfirmBatch, useEvents, useLabelBatch, useResendBatch, useVoidBatch } from '../../api/queries';
import { EventTable } from '../../components/EventTable';
import { PrintStatusChip } from '../../components/PrintStatusChip';
import { ProblemAlert } from '../../components/ProblemAlert';
import { formatTimestamp } from '../../format';
import { ReasonDialog } from './ReasonDialog';

export function LabelBatchDetailPage() {
  const { batchId = '' } = useParams();
  const batch = useLabelBatch(batchId);
  const confirm = useConfirmBatch();
  const resend = useResendBatch();
  const voidBatch = useVoidBatch();
  // A köteg teljes élete egyetlen lekérdezésben (spec 12. §: a tömeges
  // művelet eseményeit közös korrelációs azonosító köti össze).
  const events = useEvents({ batchId: batchId, page: 1, pageSize: 200 });

  const [resendOpen, setResendOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);

  if (batch.isPending) {
    return (
      <Stack sx={{ py: 6, alignItems: 'center' }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (batch.isError) return <ProblemAlert error={batch.error} />;

  const b = batch.data;
  const registeredCount = b.allocations.filter((a) => a.isRegistered).length;
  const isVoided = b.voidedAt !== null;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Button component={RouterLink} to="/cimkek" startIcon={<ArrowBackIcon />}>
          Kötegek
        </Button>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
          {LABEL_KIND_LABEL_HU[b.labelKind]} — {b.requestedCount} db
        </Typography>
        <PrintStatusChip status={b.printStatus} />
      </Stack>

      {/*
        A `hint` a szerver KÉSZ magyar mondata a következő lépésről.
        Szó szerint jelenítjük meg — ha itt újrafogalmaznánk, két helyen
        kellene karbantartani ugyanazt a döntési logikát.
      */}
      <Alert severity={b.printStatus === 'FAILED' ? 'error' : 'info'}>
        <AlertTitle>Következő lépés</AlertTitle>
        {b.hint}
      </Alert>

      {b.failureDetail && (
        <Alert severity="warning">
          <AlertTitle>A nyomtató válasza</AlertTitle>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {b.failureDetail}
          </Typography>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Row label="Létrehozva" value={`${formatTimestamp(b.createdAt)} — ${b.createdBy}`} />
          <Row
            label="Tartomány"
            value={b.firstBarcode && b.lastBarcode ? `${b.firstBarcode} – ${b.lastBarcode}` : '—'}
            mono
          />
          <Row label="PrintNode job" value={b.printNodeJobId ? String(b.printNodeJobId) : '—'} />
          <Row label="Megerősítve" value={formatTimestamp(b.confirmedAt)} />
          <Row label="Selejtezve" value={formatTimestamp(b.voidedAt)} />
          <Row label="Megjegyzés" value={b.note ?? '—'} />
          <Row
            label="Már regisztrálva"
            value={`${registeredCount} / ${b.allocations.length} sorszámra van mappa`}
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1">Műveletek</Typography>

          {confirm.isError && <ProblemAlert error={confirm.error} />}
          {resend.isError && <ProblemAlert error={resend.error} />}
          {voidBatch.isError && <ProblemAlert error={voidBatch.error} />}

          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="success"
              disabled={isVoided || b.printStatus === 'CONFIRMED' || confirm.isPending}
              onClick={() => confirm.mutate({ id: b.id })}
            >
              Megerősítem: a címkék kinyomtatva a kezemben vannak
            </Button>

            <Button variant="outlined" disabled={isVoided} onClick={() => setResendOpen(true)}>
              Újraküldés a nyomtatóra
            </Button>

            <Button
              variant="outlined"
              color="error"
              disabled={isVoided}
              onClick={() => setVoidOpen(true)}
            >
              Selejtezés
            </Button>
          </Stack>

          {/*
            A megerősítés SZÁNDÉKOSAN emberi művelet: a nyomtató nem ad
            visszajelzést arról, hogy kijött-e a papír, ezért ezt semmilyen
            kódút nem tudja eldönteni.
          */}
          <Typography variant="caption" color="text.secondary">
            A megerősítést csak azután nyomd meg, hogy fizikailag megnézted a kinyomtatott köteget.
            A rendszer nem tudja ellenőrizni, hogy a címkék tényleg kijöttek-e.
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>
          Sorszámok
        </Typography>
        <Divider />
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Vonalkód</TableCell>
                <TableCell align="right">Sorszám</TableCell>
                <TableCell>Állapot</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {b.allocations.map((a) => (
                <TableRow key={a.barcode}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{a.barcode}</TableCell>
                  <TableCell align="right">{a.sequence}</TableCell>
                  <TableCell>
                    {a.voidedAt ? (
                      <Chip size="small" label="Selejtezve" variant="outlined" />
                    ) : a.isRegistered ? (
                      <Chip size="small" color="success" label="Mappa regisztrálva" variant="outlined" />
                    ) : (
                      <Chip size="small" color="info" label="Szabad" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined">
        <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>
          A köteg élete
        </Typography>
        <Divider />
        {events.isError && <ProblemAlert error={events.error} />}
        <EventTable
          events={events.data?.items ?? []}
          showEntityColumns={false}
          emptyText="Még nincs esemény ehhez a köteghez."
        />
      </Paper>

      <ReasonDialog
        open={resendOpen}
        title="Címkeköteg újraküldése"
        description="Ugyanazokat a sorszámokat nyomtatja újra — nem foglal új számot, tehát nem éget el címkét."
        confirmLabel="Újraküldés"
        pending={resend.isPending}
        onClose={() => setResendOpen(false)}
        onSubmit={(reason) =>
          resend.mutate({ id: b.id, reason }, { onSuccess: () => setResendOpen(false) })
        }
      />

      <ReasonDialog
        open={voidOpen}
        title="Címkeköteg selejtezése"
        description={
          registeredCount > 0
            ? `Ebben a kötegben ${registeredCount} sorszámra MÁR van regisztrált mappa — azokat a rendszer kihagyja, mert egy mappára ragasztott címke visszamenőleg nem érvényteleníthető.`
            : 'A köteg sorszámai érvénytelenné válnak, és nem lesznek regisztrálhatók.'
        }
        confirmLabel="Selejtezés"
        destructive
        pending={voidBatch.isPending}
        onClose={() => setVoidOpen(false)}
        onSubmit={(reason) =>
          voidBatch.mutate({ id: b.id, reason }, { onSuccess: () => setVoidOpen(false) })
        }
      />
    </Stack>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" sx={{ minWidth: 200, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined }}>
        {value}
      </Typography>
    </Stack>
  );
}
